<template>
  <page :name="$t('Upgrade')" :title="$t('Upgrade Prodotto')" :state="productsState">
    <pf-page-section>
      <pf-alert
        v-if="product?.upgradable_products.length === 0"
        variant="danger"
        :title="$t('Attenzione: non è possibile effettuare l\'upgrade automatico con relativo storno per questo prodotto.')"
      >
        <i18n-t keypath="Aprire un {0} al supporto clienti specificando l'errore per assistenza." tag="p">
          <router-link :to="{ name: 'tickets' }">
            {{ $t('ticket') }}
          </router-link>
        </i18n-t>
      </pf-alert>

      <x-form
        v-else-if="product && valoreStorno >= 0"
        action="/api/pay_product_upgrade"
        :submit-text="$t('Conferma l\'upgrade')"
        :prevent-submit="!method || !selected || !product.id"
        style="max-width: 800px;"
      >
        <form-group
          :name="$t('Stai effettuando l\'upgrade del prodotto')"
          :helper-text="$t('In scadenza il {0}', [$date(product.data_fine)])"
          ouia-id="current-product-form-group"
        >
          {{ product.nome }} [{{ product.codice }}] {{ product.dominio }}
        </form-group>

        <form-group :name="$t('Seleziona il nuovo prodotto')">
          <pf-radio
            v-for="pu in product.upgradable_products"
            :key="pu.id"
            name="id_product_to"
            required
            :value="pu.id"
            :checked="idProductTo === pu.id"
            @change="idProductTo = pu.id"
          >
            <template #label>
              {{ pu.nome }}
              <div>{{ $t('Prezzo: {0}', [formatCurrency(pu.prezzo, login.currency)]) }}</div>
              <div>{{ $t('Codice: {0}', [pu.codice]) }}</div>
            </template>
          </pf-radio>
        </form-group>

        <template v-if="selected">
          <input type="hidden" name="id_migrated_from" :value="product.id">

          <pf-alert variant="info" :title="$t('Upgrade')">
            {{ $t('Il prodotto "{0}" verrà disdetto', [product.nome]) }}.<br>
            {{ $t('Procederemo all\'upgrade a "{0}".', [selected.nome]) }}
          </pf-alert>

          <form-group :name="$t('Riepilogo dei costi')">
            <pf-description-list
              horizontal
              class="payment-totals"
              term-width="30ch"
              style="max-width: 600px;"
            >
              <pf-description-list-group>
                <pf-description-list-term>{{ $t('Costo upgrade') }}:</pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  {{ formatCurrency(selected.prezzo, login.currency) }} <span class="vat">({{$t('IVA esclusa')}})</span>
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group v-if="valoreStorno > 0">
                <pf-description-list-term>{{ $t('Storno periodo non usufruito') }}:</pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  -{{ formatCurrency(valoreStorno, login.currency) }} <span class="vat">({{$t('IVA inclusa')}})</span>
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group v-if="totale > 0">
                <pf-description-list-term>{{ $t('Totale') }}:</pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <strong>{{ formatCurrency(totale, login.currency) }}</strong> <span class="vat">({{$t('IVA inclusa')}})</span>
                </pf-description-list-description>
              </pf-description-list-group>
            </pf-description-list>
          </form-group>

          <payment-methods
            v-model="method"
            enable-cashback
            :totale-offerta-ivato="totale"
            :currency="login.currency"
          />
        </template>
      </x-form>

      <pf-alert v-else variant="danger" :title="$t('Errore: Prodotto non trovato')" />
    </pf-page-section>
  </page>
</template>

<style scoped lang="scss">
.pf-v6-c-radio {
  margin-bottom: var(--pf-v6-global--spacer--sm);
}
</style>

<script lang="ts" setup>
import { ref, computed, type Ref } from 'vue';
import { asyncComputed } from '@common/asyncComputed';
import { formatCurrency } from '@common/utils';
import PaymentMethods, { type Method} from '../components/payment-methods.vue';
import { useLoginStore } from '@/store/login';
import { ProductOrdersResource } from '@/resources';
import { pop } from '@common/utils';
import { useRoute } from 'vue-router';

const route = useRoute();
const login = useLoginStore();

const method: Ref<Method | undefined> = ref();
const idProductTo: Ref<number | null> = ref(null);

const { state: productsState, result: product } = asyncComputed(async() => {
  const id = pop(route.params.id);
  if (!id) {
    return null;
  }
  const products = await new ProductOrdersResource().get(id);
  return products[0];
});

const selected = computed(() => product.value?.upgradable_products.find(p => p.id == idProductTo.value));
const totale = computed(() => (selected.value ? selected.value.importo : 0) - valoreStorno.value);

const valoreStorno = computed(() => product.value?.valore_storno ?? 0);
</script>
